<template>
  <div class="h-48 w-full rounded-b-lg">
    <Line
      :data="chartData"
      v-if="!UIStore.is_chart_loading"
      :options="chartOptions"
      :plugins="[fixedTooltipPlugin]"
    />
  </div>
</template>

<script setup>
import { computed, watch, ref } from "vue";
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import { useUIStore, useInvestmentStore } from "@/stores";
import { formatMonthYear } from "@/utilities";

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Filler, Legend);

const UIStore = useUIStore();
const investmentStore = useInvestmentStore();

const props = defineProps({
  type: {
    type: String,
    default: "investment-details",
  },
});

/* Gradient */
const gradientGenerator = (ctx, chartArea) => {
  if (!chartArea) return null;
  const g = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  props.type == "investor-dashboard" ||
  props.type == "investment-earnings" ||
  props.type == "investment-details"
    ? investmentStore.hasNoDistributions
      ? g.addColorStop(0, "rgba(52, 211, 153, 0.20)")
      : g.addColorStop(0, "rgba(255, 200, 0, 0.10)")
    : g.addColorStop(0, "rgba(201, 173, 125, 0.20)");
  return g;
};

const defaultData = [1000, 3000, 6000, 10000, 24000, 50000, 90000];

const values = computed(() => {
  if (investmentStore.hasNoDistributions) return defaultData;

  if (investmentStore.hasNoCompletedDistributions) {
    return investmentStore.chartData.values.slice(0, 2);
  }

  return investmentStore.chartData.values;
});

const labels = computed(() => {
  if (investmentStore.hasNoDistributions) return ["", "", "", "", "", "", ""];

  if (investmentStore.hasNoCompletedDistributions) {
    return investmentStore.chartData.labels.slice(0, 6);
  }

  return investmentStore.chartData.labels;
});

function niceNumber(range, round) {
  const exponent = Math.floor(Math.log10(range));
  const fraction = range / Math.pow(10, exponent);

  let niceFraction;

  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }

  return niceFraction * Math.pow(10, exponent);
}

function getHeadroomYAxisScale(lastValue, percent = 0.65, tickCount = 6) {
  if (!lastValue || lastValue <= 0) {
    return { yMax: 1000, stepSize: 200 };
  }

  const targetMax = lastValue / percent;

  const niceRange = niceNumber(targetMax, false);
  const stepSize = niceNumber(niceRange / (tickCount - 1), true);

  const yMax = Math.ceil(targetMax / stepSize) * stepSize;

  return { yMax, stepSize };
}

const yAxisScale = computed(() => {
  const v = values.value;

  const lastValue =
    investmentStore.hasNoCompletedDistributions && !investmentStore.hasNoDistributions
      ? (v?.[1] ?? 0)
      : (v?.at(-1) ?? 0);

  return getHeadroomYAxisScale(lastValue);
});

// Chart data
const chartData = computed(() => ({
  labels: labels.value,
  datasets: [
    {
      data: values.value,
      borderColor:
        props.type == "investor-dashboard" && !investmentStore.hasNoDistributions
          ? "#daaa00"
          : props.type == "investment-details" || investmentStore.hasNoDistributions
            ? UIStore.is_dark_mode
              ? "#34d399"
              : "#079669"
            : "#c49700",
      backgroundColor: (ctx) => {
        const { chart } = ctx;
        return gradientGenerator(chart.ctx, chart.chartArea);
      },
      segment: {
        borderDash: (ctx) => {
          if (
            (!investmentStore.hasNoDistributions && investmentStore.hasNoCompletedDistributions) ||
            (!investmentStore.hasNoDistributions && ctx.p0DataIndex === 0)
          ) {
            return [6, 6];
          }

          return [];
        },
      },
      fill: true,
      tension: 9,
      cubicInterpolationMode: "monotone",
      borderWidth: 3,

      pointRadius: (ctx) =>
        (!investmentStore.hasNoDistributions && ctx.dataIndex === values.value.length - 1) ||
        (investmentStore.hasNoCompletedDistributions &&
          !investmentStore.hasNoDistributions &&
          ctx.dataIndex === 1)
          ? 6
          : 0,
      pointBackgroundColor:
        props.type == "investor-dashboard" ? "#fff" : UIStore.is_dark_mode ? "#516679" : "#dfc695",
      pointBorderColor:
        props.type == "investor-dashboard"
          ? "#daaa00"
          : props.type == "investment-details"
            ? UIStore.is_dark_mode
              ? "#34d399"
              : "#079669"
            : "#c49700",
      pointBorderWidth: 3,
    },
  ],
}));

// watch(
//   () => investmentStore.chartData.values,
//   (values) => [
//     investmentStore.chartData.values,
//     investmentStore.hasNoCompletedDistributions,
//     investmentStore.hasNoDistributions,
//   ],
//   (values) => {
//     let yAxis = getYAxisScale(
//       investmentStore.hasNoCompletedDistributions && !investmentStore.hasNoDistributions
//         ? values[1]
//         : values[values.length - 1],
//       0.6
//     );
//     yMax.value = yAxis.yMax;
//     stepSize.value = yAxis.stepSize;
//     console.log("yMax", yMax.value, "stepSize", stepSize.value);

//     // assign or use yMax / stepSize here
//   },
//   { deep: true }
// );

// Chart options
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: investmentStore.hasNoDistributions ? false : true,
      intersect: false,
      mode: "index",
      callbacks: {
        label: (ctx) =>
          new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(ctx.parsed.y),
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: {
        callback: function (value, index, ticks) {
          let label = this.getLabelForValue(value);

          if (index === 0) {
            return "           " + label; // push first label right
          }

          return label;
        },
        maxTicksLimit: 12,
        autoSkip: true,
        color:
          props.type === "investor-dashboard"
            ? "#cbd5e1"
            : UIStore.is_dark_mode
              ? "#cbd5e1"
              : "#141414",
      },
    },
    y: {
      border: {
        dash: [12, 6],
      },
      min: 0,
      max: investmentStore.hasNoDistributions ? 100000 : yAxisScale.value.yMax,
      display: investmentStore.hasNoDistributions ? false : true,
      grid: {
        display: investmentStore.hasNoDistributions ? false : true,
        drawBorder: false,
        color:
          props.type === "investor-dashboard"
            ? "#395267"
            : UIStore.is_dark_mode
              ? "#395267"
              : "#cec2ab",
      },
      ticks: {
        display: investmentStore.hasNoDistributions ? false : true,
        stepSize: yAxisScale.value.stepSize,
        color:
          props.type === "investor-dashboard"
            ? "#cbd5e1"
            : UIStore.is_dark_mode
              ? "#cbd5e1"
              : "#141414",
        callback: (v) => `$${v / 1000}k`,
      },
    },
  },
}));

const fixedTooltipPlugin = {
  // id: "fixedTooltipPlugin",
  // afterRender(chart) {
  //   const datasetIndex = 0;
  //   const index = chart.data.labels.length - 1;
  //   const meta = chart.getDatasetMeta(datasetIndex);
  //   const point = meta.data[index];
  //   if (!point) return;
  //   chart.tooltip.setActiveElements([{ datasetIndex, index }], { x: point.x, y: point.y });
  //   chart.draw();
  // },
};
</script>

<style scoped></style>
